export interface RopePoint {
  x: number;
  y: number;
  oldX: number;
  oldY: number;
}

export interface RopePathPoint {
  x: number;
  y: number;
  tangent: number;
}

export interface RopeClosestPoint {
  x: number;
  y: number;
  distance: number;
  distanceAlong: number;
  segmentIndex: number;
}

export function pointAtDistance(points: RopePoint[], distanceAlong: number, maxLength: number): RopePathPoint {
  const target = Math.max(0, Math.min(distanceAlong, maxLength));
  let walked = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const segmentLen = Math.sqrt(dx * dx + dy * dy) || 0.0001;

    if (walked + segmentLen >= target) {
      const t = (target - walked) / segmentLen;
      return {
        x: a.x + dx * t,
        y: a.y + dy * t,
        tangent: Math.atan2(dx, dy),
      };
    }

    walked += segmentLen;
  }

  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  return { x: last.x, y: last.y, tangent: Math.atan2(last.x - prev.x, last.y - prev.y) };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// Closest approach between a moving point's path (the segment a0 -> a1 over one
// frame) and the rope polyline. Used so a fast jump can't tunnel straight
// through a vine between two frames without a catch being detected.
export function sweptClosestPoint(
  points: RopePoint[],
  a0x: number,
  a0y: number,
  a1x: number,
  a1y: number,
): RopeClosestPoint {
  const best: RopeClosestPoint = {
    x: points[0].x,
    y: points[0].y,
    distance: Infinity,
    distanceAlong: 0,
    segmentIndex: 0,
  };
  let bestDistSq = Infinity;

  const d1x = a1x - a0x;
  const d1y = a1y - a0y;
  const aa = d1x * d1x + d1y * d1y; // |motion|^2
  let walked = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const b0 = points[i];
    const b1 = points[i + 1];
    const d2x = b1.x - b0.x;
    const d2y = b1.y - b0.y;
    const e = d2x * d2x + d2y * d2y; // |ropeSeg|^2
    const segLen = Math.sqrt(e) || 0.0001;
    const rx = a0x - b0.x;
    const ry = a0y - b0.y;
    const f = d2x * rx + d2y * ry;

    let s: number;
    let t: number;
    if (aa <= 1e-9 && e <= 1e-9) {
      s = 0;
      t = 0;
    } else if (aa <= 1e-9) {
      s = 0;
      t = clamp01(f / e);
    } else {
      const c = d1x * rx + d1y * ry;
      if (e <= 1e-9) {
        t = 0;
        s = clamp01(-c / aa);
      } else {
        const bb = d1x * d2x + d1y * d2y;
        const denom = aa * e - bb * bb;
        s = denom > 1e-9 ? clamp01((bb * f - c * e) / denom) : 0;
        t = (bb * s + f) / e;
        if (t < 0) {
          t = 0;
          s = clamp01(-c / aa);
        } else if (t > 1) {
          t = 1;
          s = clamp01((bb - c) / aa);
        }
      }
    }

    const cpRx = b0.x + d2x * t;
    const cpRy = b0.y + d2y * t;
    const cpMx = a0x + d1x * s;
    const cpMy = a0y + d1y * s;
    const dx = cpRx - cpMx;
    const dy = cpRy - cpMy;
    const distSq = dx * dx + dy * dy;

    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best.x = cpRx;
      best.y = cpRy;
      best.distanceAlong = walked + segLen * t;
      best.segmentIndex = i;
    }

    walked += segLen;
  }

  best.distance = Math.sqrt(bestDistSq);
  return best;
}
